import { ArrowLeft } from "lucide-react";
import React from "react";
import { Outlet } from "react-router";
import type { MeUserDataType } from "src/app";
import { Header } from "src/components/header";
import { LeftNavigation } from "src/components/left-navigation";
import { RequireScope } from "src/components/RequireScope";
import { PageSuspense } from "src/components/ui/page-suspense";
import { Toaster } from "src/components/ui/toaster";
import { NavigationProvider, useNavigation } from "src/context/NavigationContext";

import { UnsavedChangesProvider } from "./context/UnsavedChangesContext";

// Main Knowledge page component - simplified to just render child routes
function KnowledgePageComponent() {
  return (
    <RequireScope scope="read:knowledge">
      <UnsavedChangesProvider>
        <div className="h-full w-full">
          <PageSuspense>
            <Outlet />
          </PageSuspense>
        </div>
      </UnsavedChangesProvider>
    </RequireScope>
  );
}

function MobileKnowledgeHeaderBar() {
  const { mobileConversationHeader } = useNavigation();

  if (!mobileConversationHeader) return null;

  return (
    <nav className="h-11 bg-background shadow-lg relative">
      <div className="absolute left-0 right-0 bottom-0 h-[1px] bg-border shadow-[0_0_5px_rgba(0,0,0,0.3)]"></div>
      <div className="h-full mx-auto flex w-full items-center px-2">
        <button
          onClick={mobileConversationHeader.onBack}
          className="p-1 rounded-md text-gray-400 hover:text-foreground hover:bg-gray-700 focus:outline-none transition-colors ml-1.5 mr-3"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <span className="text-base font-medium text-foreground truncate">
          {mobileConversationHeader.title}
        </span>
      </div>
    </nav>
  );
}

function KnowledgePageLayoutContent({ page, me }: { page: React.ReactElement, me: MeUserDataType }) {
  const { isLeftNavCollapsed, isMobile, mobileConversationHeader } = useNavigation();

  const showMobileHeader = isMobile && mobileConversationHeader;

  return (
    <div className="h-screen flex bg-background overflow-hidden">
      <LeftNavigation isCollapsed={isLeftNavCollapsed} />
      <div className="flex flex-col flex-1 h-screen">
        {!showMobileHeader && <Header me={me} />}
        {showMobileHeader && <MobileKnowledgeHeaderBar />}
        <main className="flex-1 flex flex-col h-full relative max-h-full overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br dark:from-slate-900/5 dark:to-slate-950/10 pointer-events-none"></div>
          {page}
        </main>
      </div>
      <Toaster />
    </div>
  );
}

// The layout wrapper for the entire knowledge page
export const getKnowledgePageLayout = (page: React.ReactElement, me: MeUserDataType) => {
  return (
    <NavigationProvider>
      <KnowledgePageLayoutContent page={page} me={me} />
    </NavigationProvider>
  );
};

// Create a new component that includes the getLayout property
const KnowledgePage = Object.assign(KnowledgePageComponent, {
  getLayout: getKnowledgePageLayout
});

export default KnowledgePage;